import { shuffle } from "lodash";
import { graphql, useStaticQuery } from "gatsby";
import Link from "gatsby-link";
import React, { Fragment, useEffect, useState } from "react";
import styled, { css } from "styled-components";
import {
  IconBook,
  IconCaret,
  IconClipboard,
  IconGraphUp,
  IconPhoneChecklist,
  IconSearch,
  H4,
  H6,
  P,
  Small,
  theme,
} from "@upsolve/ui";

import Logo from "../../../static/images/upsolve-logo.svg";
import TimesLogo from "../../../static/images/times.svg";

import getMyUpsolveEntryUrl from "../Links/getMyUpsolveEntryUrl";
import { MyUpsolveLink, MyUpsolveButtonLink } from "../Links/MyUpsolveLink";
import trackComponentAction from "../analytics/trackComponentAction";
import trackComponentImpression from "../analytics/trackComponentImpression";
import UpsolveSearchBar from "../search/UpsolveSearchBar";
import upsolveStats from "../../../content/onBuild/upsolveStats.json";
import withScrollWatcher from "../analytics/withScrollWatcher";
import useInterval from "../utils/useInterval";

function getPath() {
  return typeof window === "undefined" ? "" : window.location.pathname;
}

const headerLinkSections = [
  { value: "bankruptcyBasics", label: "Bankruptcy Basics" },
  { value: "chapter7", label: "Chapter 7 Bankruptcy" },
  { value: "wageGarnishment", label: "Wage Garnishment" },
  { value: "propertyExemptions", label: "Property & Exemptions" },
  { value: "cars", label: "Cars" },
];

const trackingProps = {
  componentName: "Header",
  componentVersion: 5,
};

/**
 * Header
 * - v0: Navbar with heavy link list under learning center
 * - v1: Navbar with search and simpler cta: 'Join Upsolve'
 * - v2: Navbar with fixed mobile footer and fixed desktop header navbar w/ bigger CTA
 * - v3: Popup message for app
 * - v4: Small popup activity shown above mobile footer
 * - v5: Added TIMES 100 inventions logo
 *
 * @component
 * @version 5
 */
const Header = (props) => {
  const { allowMobileFooter = true, onlyLogo, startTransparent = false } = props;
  const [isMenuShowing, setMenuShowing] = useState(false);
  const [isLearnPanelOpen, setLearnPanelOpen] = useState(false);
  const [isSearchOpen, setSearchOpen] = useState(false);
  const data = useStaticQuery(graphql`
    query HeaderQuery {
      articles: allContentfulLearnArticle(filter: { siteLinking: { showInSiteNav: { eq: true } } }) {
        nodes {
          title
          slug
          siteLinking {
            showInSiteNav
          }
          tags {
            value
          }
        }
      }
    }
  `);
  const linksToPagesInMenu = [
    // Manual Additions (landing pages)
    {
      title: "The Ultimate Guide to Bankruptcy",
      path: "/bankruptcy/",
      tags: [
        {
          value: "bankruptcyBasics",
        },
      ],
    },
    // From LearnArticle SiteLinking Component
    ...data.articles.nodes.map((a) => ({
      ...a,
      path: `/learn/${a.slug}/`,
    })),
  ];

  // RENDER
  // - Only Logo (needed for landing pages like accessibility awards)
  if (onlyLogo) {
    return (
      <NavBar>
        <div className="nav__lead" style={{ margin: "0 auto" }}>
          <span>
            <Link to="/">
              Upsolve
              <Logo />
            </Link>
          </span>
          <Small>Civil Rights Should Be Free</Small>
        </div>
      </NavBar>
    );
  }
  // - Normal
  return (
    <div>
      <NavBar startTransparent={startTransparent} atTop={props.scrollWatcher.scrollPosition === 0}>
        <div className="nav__lead">
          <span role="button" aria-label="Toggle Menu">
            <Link to="/">
              Upsolve
              <Logo />
            </Link>
          </span>
          <Small>Civil Rights Should Be Free</Small>
        </div>

        <Link className="nav__highlight" aria-busy="false" to="/learn/blog-2-time-2020-inventions/">
          <TimesLogo />
          <Small>2020 Best Invention</Small>
        </Link>

        <nav className={`nav__links ${isMenuShowing ? "show" : ""}`}>
          <div>
            <Link aria-busy="false" to="/learn/should-i-file-for-chapter-7-bankruptcy/">
              About Bankruptcy
            </Link>
          </div>
          <div>
            <Link aria-busy="false" to="/learn/how-to-file-bankruptcy/">
              How to File
            </Link>
          </div>
          <div>
            <Link aria-busy="false" to="/learn/transparency/">
              How it's Free
            </Link>
          </div>
          <div onMouseEnter={() => setLearnPanelOpen(true)} onMouseLeave={() => setLearnPanelOpen(false)}>
            <Link aria-busy="false" to="/learn/">
              Learn{" "}
              <span className="caret">
                <IconCaret />
              </span>
            </Link>
            <div className={`nav__links__list ${isLearnPanelOpen ? "show" : ""}`}>
              {headerLinkSections.map((headerLinkSection) => (
                <div key={headerLinkSection.value}>
                  <H6 as="label">{headerLinkSection.label}</H6>
                  <ul key={headerLinkSections.values}>
                    {linksToPagesInMenu
                      .filter((node) => node.tags.some((tag) => tag.value === headerLinkSection.value))
                      .map((node) => (
                        <li key={node.path}>
                          <Link
                            aria-busy="false"
                            to={node.path}
                            onClick={() => {
                              setLearnPanelOpen(false);
                              setMenuShowing(false);
                            }}
                          >
                            {node.title}
                          </Link>
                        </li>
                      ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </nav>

        <div className="nav__ctas">
          <span
            className="nav__ctas__search"
            onClick={(ev) => {
              if (!getPath().includes("/search")) setSearchOpen(true);
            }}
          >
            <IconSearch />
          </span>
          <MyUpsolveLink aria-busy="false" className="login-link" href={`${UPSOLVE_FILER_URL}/login`}>
            Login
          </MyUpsolveLink>
          <MyUpsolveLink
            aria-busy="false"
            className="screener-link"
            href={getMyUpsolveEntryUrl({ triggeredByActionId: "header.myUpsolveNavigation" })}
            inverted
            onClick={() =>
              trackComponentAction({
                ...trackingProps,
                actionId: "header.myUpsolveNavigation",
                actionMethod: "click",
                pageContext: props.trackingPageContext
              })
            }
          >
            Free Filing Tool <IconCaret />
          </MyUpsolveLink>
        </div>
      </NavBar>

      {isSearchOpen && (
        <UpsolveSearchBar
          autoFocus={true}
          onClose={() => setSearchOpen(false)}
          onSearch={() => setSearchOpen(false)}
          position="navbar"
        />
      )}

      {isMenuShowing && <MobileMenuBackground onClick={() => setMenuShowing(false)} />}

      {allowMobileFooter !== false && (
        <Fragment>
          <MobileFooterNav>
            <Link
              className={
                getPath().includes("/learn/") && !getPath().includes("/learn/how-to-file-bankruptcy/") ? "active" : ""
              }
              onClick={(ev) => {
                if (getPath().includes("/learn/")) {
                  ev.preventDefault();
                  setMenuShowing(!isMenuShowing);
                }
              }}
              to="/learn/"
            >
              <div>
                <IconBook />
                Free Articles
              </div>
            </Link>
            <MyUpsolveLink
              href={getMyUpsolveEntryUrl({
                triggeredByActionId: "header.fixedFooter.myUpsolveNavigation",
              })}
              onClick={() =>
                trackComponentAction({
                  ...trackingProps,
                  actionId: "header.fixedFooter.myUpsolveNavigation",
                  actionMethod: "click",
                  pageContext: props.trackingPageContext,
                })
              }
            >
              <div className="checklist">
                <IconPhoneChecklist />
                Bankruptcy Tool
              </div>
            </MyUpsolveLink>
            <Link
              className={getPath().includes("/learn/how-to-file-bankruptcy/") ? "active" : ""}
              to="/learn/how-to-file-bankruptcy/"
            >
              <div>
                <IconClipboard />
                Filing Guide
              </div>
            </Link>
          </MobileFooterNav>

          <NavPopUp trackingPageContext={props.trackingPageContext} />
        </Fragment>
      )}

      {/* <StepActivityHinter /> */}
    </div>
  );
};

const TRANSITION_TIMER = "250ms";

const NavBar = styled.header`
  background: ${(props) => props.theme.colors.white[900]};
  box-shadow: 0 5px 15px -5px rgba(0, 0, 0, 0.025);
  display: flex;
  justify-content: space-between;
  width: 100%;
  z-index: 999;

  a {
    color: ${(props) => props.theme.colors.brand[500]};
    &:hover {
      color: ${(props) => props.theme.colors.brand[100]};
    }
    font-size: 14px;
    button {
      font-weight: 700;
    }
  }

  .nav__lead,
  .nav__highlight,
  .nav__links,
  .nav__ctas {
    flex-grow: 1;
    height: 54px;
  }

  .nav__lead {
    max-width: 150px;
    display: inline-flex;
    align-items: center;
    margin-left: 1em;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    small {
      font-size: 9px;
      color: ${(props) => props.theme.colors.brand[100]};
      margin: 0 12px;
      @media (max-width: ${(props) => props.theme.breakpoints[500]}) {
        margin: 0 3px;
      }
    }
    .icon-menu {
      width: 24px;
      max-width: 44px;
      display: none;
      @media (max-width: ${(props) => props.theme.breakpoints[750]}) {
        display: initial;
      }
    }
    svg {
      display: block;
      height: 32px;
      width: 140px;
      margin-top: -2px;
      @media (max-width: ${(props) => props.theme.breakpoints[500]}) {
        height: 28px;
        width: 120px;
        margin-top: 0;
      }
      path {
        fill: ${(props) => props.theme.colors.brand[500]};
      }
    }
    span > a {
      display: flex;
      font-size: 0;
    }
    @media (max-width: ${(props) => props.theme.breakpoints[500]}) {
      margin-left: 0.9em;
    }
  }
  .nav__highlight {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex-grow: 0;
    padding-top: 3px;
    svg {
      height: 20px;
      margin: 0 auto;
    }
    small {
      font-size: 9px;
      color: ${(props) => props.theme.colors.red[500]};
      margin: 0 12px;
      text-align: center;
      @media (max-width: ${(props) => props.theme.breakpoints[500]}) {
        margin: 0 3px;
      }
    }
    @media (max-width: ${(props) => props.theme.breakpoints[750]}) {
      position: absolute;
      left: 160px;
    }
    @media (max-width: ${(props) => props.theme.breakpoints[500]}) {
      position: absolute;
      left: 140px;
    }
  }
  .nav__links {
    .caret > svg {
      height: 8px;
      width: 8px;
      path {
        fill: ${(props) => props.theme.colors.brand[500]};
      }
    }
    // Desktop (Wide)
    & > .nav__links__header {
      display: none;
    }
    @media (min-width: ${(props) => props.theme.breakpoints[750]}) {
      display: flex;
      flex-grow: 1;
      justify-content: center;
      & > div {
        display: flex;
        align-items: center;
        & > a {
          padding: 0.75em;
          font-weight: 500;
        }
      }
    }
    // Mobile (Slim)
    @media (max-width: ${(props) => props.theme.breakpoints[750]}) {
      position: fixed;
      overflow-y: scroll;
      width: 100vw;
      height: 90vh;
      z-index: 998;
      transform: translateY(100vh);
      background: ${(props) => props.theme.colors.white[900]};
      border-top: 4px solid ${(props) => props.theme.colors.white[500]};
      padding: 0 0 3em;
      &.show {
        transform: translateY(10vh);
      }
      & > .nav__links__header {
        display: flex;
        justify-content: space-between;
      }
      & > div {
        padding: 1em;
        &:first-of-type {
          margin-top: 1em;
        }
        &:last-of-type {
          margin-bottom: 2.5em;
        }
        &:not(:last-of-type) {
          border-bottom: 1px solid ${(props) => props.theme.colors.white[300]};
        }
        & > a {
          font-size: 18px;
          color: ${(props) => props.theme.colors.brand[500]};
        }
      }
    }
  }
  .nav__links__list {
    // Desktop (Wide)
    @media (min-width: ${(props) => props.theme.breakpoints[750]}) {
      display: none;
      width: 100vw;
      position: absolute;
      z-index: 998;
      left: 0;
      right: 0;
      top: 54px;
      background: ${(props) => props.theme.colors.white[800]};
      border-top: 1px solid ${(props) => props.theme.colors.white[500]};
      box-shadow: 0 5px 15px -5px rgba(0, 0, 25, 0.2);
      padding: 0.5em 2em;
      &.show {
        display: grid;
        grid-auto-flow: column;
        grid-template-columns: repeat(5, 1fr);
      }
      a {
        font-size: 13px;
      }
      div {
        margin: 1em 0;
      }
      ul {
        padding: 0 0.5em 0 1em;
        list-style-type: disc;
        flex: 1;
      }
      li {
        margin-bottom: 0.2em;
        color: ${(props) => props.theme.colors.brand[500]};
      }
    }
    // Mobile (Slim)
    @media (max-width: ${(props) => props.theme.breakpoints[750]}) {
      div {
        margin: 1em 0;
      }
      ul {
        padding: 0;
        margin: 0;
        &:first-of-type {
          margin-top: 1em;
        }
        &:last-of-type {
          margin-bottom: 1em;
        }
      }
      li {
        margin-bottom: 0.65em;
        list-style-type: disc;
        color: ${(props) => props.theme.colors.brand[500]};
        margin-left: 1.25em;
        a {
          font-size: 15px;
        }
      }
    }
  }
  .nav__ctas {
    display: flex;
    flex-grow: 0;
    align-items: center;
    justify-content: flex-end;
    & > * {
      margin-right: 0.5em;
    }
    & > a {
      padding-left: 6px;
      padding-right: 6px;
      color: ${(props) => props.theme.colors.brand[500]};
      font-weight: 500;
      &:last-child {
        margin-right: 1.25em;
      }
    }
    .screener-link {
      border: 1px solid ${(props) => props.theme.colors.brand[500]};
      border-radius: 4px;
      svg {
        height: 12px;
        width: 12px;
        transform: rotate(-90deg);
        position: relative;
        top: 1px;
        path {
          fill: ${(props) => props.theme.colors.brand[500]};
        }
      }
      @media (max-width: ${(props) => props.theme.breakpoints[750]}) {
        display: none !important;
      }
    }
  }
  .nav__ctas__search {
    height: 36px;
    width: 36px;
    margin: 4px 0;
    padding: 7px;
    display: flex;
    align-items: center;
    justify-content: center;
    svg {
      height: 32px;
      min-height: 32px;
      position: relative;
      top: -1px;
    }
    path {
      fill: ${(props) => props.theme.colors.gray[900]};
    }
    &:hover {
      fill: ${(props) => props.theme.colors.brand[500]};
      cursor: pointer;
    }
  }
  & > a {
    @media (max-width: ${(props) => props.theme.breakpoints[750]}) {
      display: none;
    }
  }

  // Handle transparent background options
  transition: background ${TRANSITION_TIMER}, box-shadow ${TRANSITION_TIMER};
  background: white;
  .nav__lead svg path,
  .nav__links .caret svg > path,
  .nav__ctas__search svg path,
  .nav__links > div > a,
  div.nav__ctas > a {
    transition: fill ${TRANSITION_TIMER}, color ${TRANSITION_TIMER}, border-color ${TRANSITION_TIMER};
  }
  ${(props) => {
    if (props.startTransparent && !props.atTop) {
      return css`
        background: ${(props) => props.theme.colors.white[900]};
        box-shadow: 0 5px 15px -5px rgba(0, 0, 0, 0.05);
      `;
    }
    if (props.startTransparent && props.atTop) {
      return css`
        background: transparent;
        box-shadow: none;
        .nav__lead {
          small {
            color: rgba(255, 255, 255, 0.7);
          }
        }
        .nav__lead svg path,
        .nav__links .caret svg > path,
        .nav__ctas__search svg path {
          fill: #fff;
        }
        .nav__links > div > a {
          color: rgba(255, 255, 255, 0.8);
          &:hover {
            color: rgba(255, 255, 255, 1);
          }
        }
        div.nav__ctas > a {
          color: white;
          border-color: white;
        }
      `;
    }
  }}
`;

const MobileFooterNav = styled.div`
  display: none;
  position: fixed;
  z-index: 999;
  bottom: 0;
  left: 0;
  right: 0;
  height: 82px;
  max-height: 82px;
  background: white;
  box-shadow: 0 0 32px 12px rgba(0, 0, 55, 0.15);
  @media (max-width: ${(props) => props.theme.breakpoints[750]}) {
    display: grid;
    grid-auto-flow: column;
    grid-template-columns: repeat(3, 1fr);
    padding: 0 1.5em;
  }
  @media (max-width: 360px) {
    padding: 0 0.25em;
  }
  & > a {
    margin: 0 6px;
    padding: 6px 0 3px;
    border-bottom: 3px solid ${(props) => props.theme.colors.gray[900]};
    &.active {
      div {
        color: ${(props) => props.theme.colors.brand[500]};
      }
      svg path {
        fill: ${(props) => props.theme.colors.brand[500]};
      }
      border-bottom: 3px solid ${(props) => props.theme.colors.brand[500]};
    }
  }
  & > a,
  div {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    font-size: 12px;
    color: ${(props) => props.theme.colors.gray[500]};
    svg {
      height: 26px;
      path {
        fill: ${(props) => props.theme.colors.gray[500]};
      }
    }
  }
  .checklist svg {
    height: 37px;
    width: 100%;
  }
`;

const MobileMenuBackground = styled.div`
  z-index: 995;
  min-width: 100vw;
  width: 100vw;
  min-height: 100vh;
  height: 110vh;
  position: fixed;
  background: ${(props) => props.theme.colors.black[100]};
  opacity: 0.65;
  top: -20vh;
`;

const popUpTrackingProps = {
  componentName: "NavPopUp",
  componentVersion: 0,
};

/**
 * NavPopUp
 * - v0: Popup for nav
 *
 * @component
 * @version 0
 */
const NavPopUp = (props) => {
  const [hasFixedFooter, setHasFixedFooter] = useState(false);
  const [isShowing, setIsShowing] = useState(false);
  function close() {
    trackComponentAction({
      ...popUpTrackingProps,
      actionId: "navPopUp.close",
      actionMethod: "click",
      pageContext: props.trackingPageContext
    });
    setIsShowing(false);
  }
  useEffect(() => {
    setHasFixedFooter(window.innerWidth < Number(theme.breakpoints[750].replace("px", "")));
    setTimeout(() => {
      if (typeof window === "undefined") return;
      const timeLastShown = localStorage.getItem("Upsolve.navPopUpShown")
        ? Number(localStorage.getItem("Upsolve.navPopUpShown"))
        : null;
      // Show no more than once every two minutes
      if (!timeLastShown || new Date().getTime() - 120 * 1000 > timeLastShown) {
        setIsShowing(true);
        trackComponentImpression({
          ...popUpTrackingProps,
          pageContext: props.trackingPageContext
        });
        localStorage.setItem("Upsolve.navPopUpShown", new Date().getTime());
      }
    }, 8000);
  }, []);

  return (
    hasFixedFooter &&
    isShowing && (
      <StyledNavPopUp onClick={(ev) => ev.preventDefault()}>
        <div className="hint__demo" onClick={close}>
          <div className="image" onClick={(ev) => ev.stopPropagation()} />
        </div>
        <div className="hint__panel">
          <div className="hint__author" />
          <H4>Considering Bankruptcy?</H4>
          <P>
            <b>
              Our free tool has helped{" "}
              {upsolveStats.filings.total.toLocaleString(undefined, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              })}
              + families file bankruptcy on their own.
            </b>{" "}
            We're funded by Harvard University and will never ask you for a credit card or payment.
          </P>
          <MyUpsolveButtonLink
            size="lg"
            width="100%"
            href={getMyUpsolveEntryUrl({
              triggeredByActionId: "navPopUp.myUpsolveNavigation",
            })}
            onClick={() => {
              trackComponentAction({
                ...popUpTrackingProps,
                actionId: "navPopUp.myUpsolveNavigation",
                actionMethod: "click",
                pageContext: props.trackingPageContext
              });
            }}
          >
            Explore Free Tool
          </MyUpsolveButtonLink>
          <u onClick={close}>No Thanks</u>
        </div>
        <div className="hint__background" onClick={close} />
      </StyledNavPopUp>
    )
  );
};

const StyledNavPopUp = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 64px;
  z-index: 997;
  display: flex;
  flex-direction: column;
  align-items: center;
  .hint__demo {
    height: 240px;
    padding: 0 5em;
    margin-bottom: -1.6em;
    width: 100%;
    max-width: 400px;
    .image {
      background: url("${require("../../../static/images/phone-demo-short.png")}");
      background-size: cover;
      background-repeat: no-repeat;
      height: 100%;
      width: 100%;
    }
  }
  .hint__author {
    height: 40px;
    width: 40px;
    position: absolute;
    top: 220px;
    left: 0.75em;
    border-radius: 50%;
    background-image: url("${require("../../../static/images/mary-g.png")}");
    background-size: cover;
    background-position: center;
    box-shadow: 0 1px 8px rgba(0, 2, 50, 0.3);
  }
  .hint__panel {
    box-shadow: 0 4px 40px 0 rgba(0, 0, 25, 0.5);
    margin: 1.5em 2.5em;
    height: auto;
    background: white;
    border-radius: 8px;
    padding: 1.5em;
    display: flex;
    flex-direction: column;
    align-items: center;
    h4 {
      border-bottom: 2px solid ${(props) => props.theme.colors.white[300]};
      padding: 0 0 12px;
    }
    u {
      text-align: center;
      margin-top: 8px;
      margin-bottom: -4px;
      z-index: 95;
      &:hover {
        cursor: pointer;
      }
    }
    p {
      font-size: 15px;
      line-height: 140%;
      letter-spacing: -0.1px;
    }
    @media (max-width: ${(props) => props.theme.breakpoints[300]}) {
      margin: 1.5em 1em;
    }
    &:after {
      content: "";
      height: 40px;
      width: 40px;
      position: absolute;
      bottom: 12px;
      transform: rotate(45deg);
      background: white;
    }
  }
  .hint__background {
    position: fixed;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.25);
  }
  .hint__demo,
  .hint__author,
  .hint__panel {
    z-index: 100;
  }
  .hint__background {
    z-index: 99;
  }
  @media (min-width: ${(props) => props.theme.breakpoints[500]}) {
    .hint__demo {
      height: 320px;
      width: 320px;
      max-width: 320px;
      padding: 0;
    }
    .hint__author {
      display: none;
    }
  }
  @media (min-width: ${(props) => props.theme.breakpoints[750]}) {
    display: none;
  }
`;

function formatNumber(num) {
  return Number(num).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function getStatTextSnippet() {
  return shuffle([
    `${upsolveStats.stepActivity.usersDischarged} Upsolvers have discharged their debt`,
    `${upsolveStats.stepActivity.interviewsStartedThisWeek} Upsolvers started filing bankruptcy this week`,
    `${upsolveStats.stepActivity.usersGotPacketsThisWeek} Upsolvers got bankruptcy forms this week`,
    `Upsolve's discharged $${formatNumber(upsolveStats.debtCleared.total)} of debt`,
  ])[0];
}

/**
 * StepActivityHints
 * - v0: Slim revealing of step activity on mobile
 *
 * @component
 * @version 0
 */
const StepActivityHinter = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [statSnippet, setStatSnippet] = useState(getStatTextSnippet());
  useInterval(() => {
    setIsVisible(true);
    setStatSnippet(getStatTextSnippet());
    setTimeout(() => {
      setIsVisible(false);
    }, 9000);
  }, 10000);

  return (
    <StyledStepActivityHinter isVisible={isVisible}>
      <IconGraphUp /> <Small>{statSnippet}</Small>
    </StyledStepActivityHinter>
  );
};

const StyledStepActivityHinter = styled.div`
  transition: bottom 1s;
  width: 100%;
  position: fixed;
  z-index: 100;
  bottom: -82px;
  left: 0;
  right: 0;
  background: ${(props) => props.theme.colors.brand[500]};
  border-top: 1px solid ${(props) => props.theme.colors.brand[700]};
  box-shadow: 0 0 32px 12px rgba(0, 0, 55, 0.15);
  display: none;
  @media (max-width: ${(props) => props.theme.breakpoints[750]}) {
    display: flex;
  }
  justify-content: center;
  align-items: center;
  small {
    color: white;
    font-weight: 500;
    letter-spacing: -0.05;
  }
  height: 40px;
  svg {
    height: 14px;
    width: 14px;
    margin-right: 7px;
    opacity: 0.7;
    path {
      fill: white;
    }
  }
  ${(props) => {
    if (props.isVisible) {
      return css`
        bottom: 82px;
      `;
    }
  }}
`;

export default withScrollWatcher(Header);
